import appConfig from 'config/appConfig';
import { logger } from '@finance/shared/dist/utils/logger';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Checks if code is running on the client side
 * @returns {boolean} true if running in browser environment
 */
export const isClientSide = () => typeof window !== 'undefined';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stringify(obj: any) {
    try {
        return JSON.stringify(obj);
    } catch (error) {
        return `unable to stringify: '${error}'`;
    }
}

const sessionContext = new Map<string, string | null>();

export function setSessionContext(contextRecord: Record<string, string | null>) {
    Object.entries(contextRecord).forEach(([key, value]) => {
        sessionContext.set(key, value);
    });
}

function reportToDataDog(
    error: Error | string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: { [key: string]: any }
) {
    if (!datadogRum || !datadogRum.addError) {
        return;
    }
    datadogRum.addError(error, context);
}

interface ErrorProps {
    error: Error | string;
    message?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context?: { [key: string]: any };
}

// eslint-disable-next-line no-console
const originalConsoleError = logger.error;
/**
 * Custom error logging function that extends the default logger behavior
 * by tracking client-side errors through DataDog before passing them to
 * the original logger.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const logError = (...args: any[]) => {
    const [maybeContext, errorMessage, ...extraArgs] = args;
    /*
        logger.error can be called in 2 ways:
        logger.error(message)
        logger.error(object, message)

        so we need to make sure we handle both cases.
     */
    // if there is a 2nd argument, assume that's the actual error message
    const error = errorMessage || maybeContext;
    // if the first argument is a string, assume there is no context
    const context = typeof maybeContext == 'string' ? {} : maybeContext;

    if (isClientSide()) {
        trackError({ error, context });
    }

    originalConsoleError.apply(logger, [maybeContext, errorMessage, ...extraArgs]);
};
// eslint-disable-next-line no-console,@typescript-eslint/ban-ts-comment
// @ts-ignore
logger.error = logError;

export function trackError(errorProps: ErrorProps) {
    const { error, message, context = {} } = errorProps;

    let errorToPass;
    let contextToPass;

    if (error instanceof Error) {
        errorToPass = message
            ? new Error(message, {
                  cause: error
              })
            : error;
        contextToPass = context;
    } else {
        errorToPass = message ? new Error(message) : error;
        contextToPass = message ? { ...context, cause: stringify(error) } : context;
    }

    reportToDataDog(errorToPass, {
        ...contextToPass,
        ...Object.fromEntries(sessionContext)
    });

    if (!appConfig.isProductionEnv) {
        // eslint-disable-next-line no-console
        console.error({ ...contextToPass, error }, message);
    }
}
